'use client';

import * as React from 'react';
import * as amplitude from '@amplitude/analytics-browser';
import { Experiment } from '@amplitude/experiment-js-client';

import type { Account } from '@/types/v2/account';
import type { Community } from '@/types/v2/community';
import type { User } from '@/types/v2/user';
import { config } from '@/config';
import { authClient } from '@/lib/v2/auth/client';
import { logger } from '@/lib/v2/default-logger';
import type { Unit } from '@/types/v2/unit';

import type { UserContextValue } from './types';

export const UserContext = React.createContext<UserContextValue | undefined>(undefined);

export interface UserProviderProps {
  children: React.ReactNode;
}

export function UserProvider({ children }: UserProviderProps): React.JSX.Element {
  const [state, setState] = React.useState<{
    user: User | null;
    account: Account | null;
    communities: Community[] | null;
    community: Community | null;
    units: Unit[] | null;
    error: string | null;
    isLoading: boolean;
    setCommunity: (community: Community, refresh: boolean) => void;
  }>({
    user: null,
    account: null,
    communities: null,
    community: null,
    units: null,
    error: null,
    isLoading: true,
    setCommunity: () => {},
  });

  const setCommunity = React.useCallback(
    async (community: Community, refresh = true) => {
      const currentCommunity = state.community;
      setState((prev) => ({ ...prev, isLoading: refresh }));
      try {
        await authClient.setUserCommunity(community);
        setState((prev) => ({ ...prev, community, isLoading: false }));
      } catch (err) {
        logger.error(err);
        setState((prev) => ({
          ...prev,
          community: currentCommunity,
          isLoading: false,
        }));
      }
    },
    [state.community]
  );

  const checkSession = React.useCallback(async (): Promise<void> => {
    try {
      const { data, error } = await authClient.getUser();
      if (error) {
        logger.error(error);
        setState((prev) => ({
          ...prev,
          user: null,
          account: null,
          communities: null,
          community: null,
          units: null,
          accessToken: null,
          error: 'Something went wrong',
          isLoading: false,
        }));
        return;
      }
      amplitude.init(config.amplitude.apiKey);
      amplitude.setUserId(data?.user?.id);
      const experiment = Experiment.initializeWithAmplitudeAnalytics(config.amplitude.apiKey);
      experiment.setUser({
        user_id: data?.user?.id,
        user_properties: {
          email: data?.user?.email ?? '',
          name: `${data?.user?.first_name ?? ''  } ${  data?.user?.last_name ?? ''}`,
        },
      });
      setState((prev) => ({
        ...prev,
        user: data?.user ?? null,
        account: data?.account ?? null,
        communities: data?.communities ?? null,
        community: data?.community ?? null,
        units: data?.units ?? null,
        error: null,
        isLoading: false,
      }));
    } catch (err) {
      logger.error(err);
      setState((prev) => ({
        ...prev,
        user: null,
        account: null,
        communities: null,
        community: null,
        units: null,
        accessToken: null,
        error: 'Something went wrong',
        isLoading: false,
      }));
    }
  }, []);

  React.useEffect(() => {
    checkSession().catch((err) => {
      logger.error(err);
    });
  }, []);


  return (
    <UserContext.Provider value={{ ...state, checkSession, setCommunity }}>{children}</UserContext.Provider>
  );
}

export const UserConsumer = UserContext.Consumer;
